import {
  toWSDateString,
  WSAlert,
  WSElement,
  WSEmptyState,
  WSFilters,
  WSLoader,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency, Payout, PayoutStatus } from "@wingspanhq/wingspan-api-sdk";
import { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { queriesPayouts } from "@query";
import { PAYOUTS_PATH } from "../..";
import { EmptySpaceErrorMessage } from "../../../../shared/components/EmptyStateErrorMessage";
import { useUrlQueryFilters } from "../../../../utils/router";
import {
  mapAndRevertParamsSortToTable,
  mapAndRevertTableSortToParams
} from "../../../../utils/tableSortingHelpers";
import { usePayoutsBetaModal } from "../../components/PayoutsBetaModal/usePayoutsBetaModal";
import { useWherePayoutModal } from "../../components/WherePayoutModal/useWherePayoutModal";
import { getPayoutStatusTableCellProps } from "../../utils/getPayoutsStatusPillProps";
import { sortData } from "../../utils/sortData";
import { getPayoutNet } from "../../utils/utils";

export type SortKey = "createdAt" | "currentPeriodBalance" | string;

type Filters = {
  status?: PayoutStatus[];
};

type PayoutsFilters = {
  filters: Filters;
  sort: Record<SortKey, "asc" | "desc">;
};

const DEFAULT_FILTERS: PayoutsFilters = {
  filters: {},
  sort: { createdAt: "desc" }
};

export const RoutePayoutsDashboard: React.FC<RouteComponentProps> = () => {
  const history = useHistory();
  const queryPayouts = queriesPayouts.listPayouts.useQueryAll();

  const [showBetaInfo, setShowBetaInfo] = useState<boolean>(true);

  const wherePayoutModal = useWherePayoutModal();
  const payoutsBetaModal = usePayoutsBetaModal();

  const { filters, setFilters } =
    useUrlQueryFilters<PayoutsFilters>(DEFAULT_FILTERS);

  const onSetFilters = (filterValue: Filters) => {
    setFilters({
      ...filters,
      filters: filterValue
    });
  };

  const sortByAmount = () => {
    setFilters({
      ...filters,
      sort: {
        currentPeriodBalance: mapAndRevertTableSortToParams(
          filters.sort?.currentPeriodBalance
        )
      }
    });
  };
  const sortByDate = () => {
    setFilters({
      ...filters,
      sort: {
        createdAt: mapAndRevertTableSortToParams(filters.sort?.createdAt)
      }
    });
  };

  const filterByPayout = (payout: Payout) => {
    const { status } = filters.filters;

    if (Array.isArray(status) && status.length) {
      if (payout?.status && !status.includes(payout.status)) {
        return false;
      }
    }

    return true;
  };

  const columns: Array<WSTableColumn<Payout>> = [
    {
      config: {
        header: {
          text: "Initiated date"
        },
        onColumnSort: sortByDate,
        sortDirection: mapAndRevertParamsSortToTable(filters?.sort?.createdAt),
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: ({ data }) => (
        <WSTableCell
          text={`${toWSDateString(data.events.createdAt, "monthDayYear")}`}
        />
      )
    },
    {
      config: {
        header: {
          text: "Description"
        },
        gridTemplateSizeMax: "1fr",
        hideOnScreens: ["XS"]
      },
      renderFunction: () => <WSTableCell text={"Wingspan Payout"} />
    },
    {
      config: {
        header: {
          text: "Invoices"
        },
        hideOnScreens: ["XS"],
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: ({ data }) => {
        return <WSTableCell text={String(data.itemCounts.payout)} />;
      }
    },
    {
      config: {
        header: {
          text: "Amount"
        },
        onColumnSort: sortByAmount,
        sortDirection: mapAndRevertParamsSortToTable(
          filters?.sort?.currentPeriodBalance
        ),
        hideOnScreens: ["XS"],
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: ({ data }) => {
        const payoutTotal = getPayoutNet(data);
        const currency = (data.currency ?? Currency.USD) as Currency;

        return <WSTableCell text={formatMoney(payoutTotal, currency)} />;
      }
    },
    {
      config: {
        header: {
          text: "Status"
        },
        hideOnScreens: ["XS"],
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: ({ data }) => (
        <WSTableCell {...getPayoutStatusTableCellProps(data)} />
      )
    }
  ];

  const pageActions = useMemo(() => {
    const actions: WSPageToolbarAction[] = [
      {
        label: "Learn more",
        buttonKind: "Secondary",
        onClick: () => {
          payoutsBetaModal.open();
        },
        hideButtonOn: false
      }
    ];

    return actions;
  }, [payoutsBetaModal]);

  const renderContent = () => {
    if (queryPayouts.isError) {
      return <EmptySpaceErrorMessage onRetry={() => queryPayouts.refetch()} />;
    }

    if (queryPayouts.isInitialLoading) {
      return (
        <WSElement my="5XL">
          <WSLoader.Spinner />
        </WSElement>
      );
    }

    const queryPayoutsData = queryPayouts.flatData ?? [];
    const tableList = queryPayoutsData
      .filter(filterByPayout)
      .sort(sortData(filters.sort!))
      .map(payout => ({
        id: payout.id,
        data: payout
      }));

    return (
      <>
        <WSFilters<Filters>
          values={filters.filters}
          onFilter={onSetFilters}
          info={{ count: tableList.length }}
          primaryFilters={[
            {
              name: "status",
              title: "Status",
              type: "checkbox",
              options: [
                {
                  label: "Complete",
                  name: PayoutStatus.COMPLETE
                },
                {
                  label: "Open",
                  name: PayoutStatus.OPEN
                },
                {
                  label: "Processing",
                  name: PayoutStatus.PROCESSING
                },
                {
                  label: "Failed",
                  name: PayoutStatus.FAILED
                }
              ]
            }
          ]}
        />
        {queryPayoutsData.length === 0 ? (
          <WSEmptyState
            type="search"
            title="No payouts available yet"
            description="Payouts will appear here once available."
          />
        ) : tableList.length === 0 ? (
          <WSEmptyState
            type="search"
            title="No results"
            description="Try adjusting your filters to find what you're looking for. Clear filters to see all items."
            actions={{
              buttons: [
                {
                  label: "Clear filters",
                  kind: "Secondary",
                  onClick: () => {
                    setFilters(DEFAULT_FILTERS);
                  }
                }
              ]
            }}
          />
        ) : (
          <>
            {showBetaInfo && (
              <WSAlert
                mt="L"
                theme="info"
                icon="info-circle"
                size="M"
                title="Introducing the Payouts (Beta) feature"
                onDismiss={() => {
                  setShowBetaInfo(false);
                }}
              >
                The payouts feature provides visibility to batched payouts which
                aggregate payments paid out to you on a daily basis reducing the
                number of transactions to keep track of.
                <WSElement mt="M" />
                <b>Batched payouts:</b> payouts are sent in batches and details
                can be viewed from Nov. 1, 2024 onward.
                <WSElement mt="M" />
                <b>Instant payouts:</b> currently, instant payouts are not part
                of batched payouts. Instant payouts can be tracked on the
                invoicing page.
              </WSAlert>
            )}
            <WSTable<Payout>
              mt="XL"
              showHeader={true}
              rowMenuActions={rowItem => [
                {
                  label: "Where is my payout?",
                  onClick: async () => {
                    await wherePayoutModal.open();
                  }
                }
              ]}
              columns={columns}
              onRowClick={item => {
                history.push(`${PAYOUTS_PATH}/${item.id}`, {
                  backPath: `${PAYOUTS_PATH}`
                });
              }}
              tableData={tableList}
            />
          </>
        )}
      </>
    );
  };

  return (
    <WSPage>
      <WSPageToolbar title={"Payouts (Beta)"} actions={pageActions} />
      {renderContent()}
    </WSPage>
  );
};
